import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { ThumbnailVariant } from '../types/ThumbnailVariant';

interface EditVariantDialogProps {
  variant: ThumbnailVariant | null;
  open: boolean;
  onClose: () => void;
  onSave: (variantId: string, label: string, isActive: boolean) => Promise<void>;
}

export function EditVariantDialog({ variant, open, onClose, onSave }: EditVariantDialogProps) {
  const [label, setLabel] = useState('');
  const [isActive, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  // Update local state when variant changes
  useEffect(() => {
    if (variant) {
      setLabel(variant.label || '');
      setEnabled(variant.isActive);
    }
  }, [variant]);

  const handleSave = async () => {
    if (!variant) return;

    setSaving(true);
    try {
      await onSave(variant.id, label, isActive);
      onClose();
    } catch (error) {
      console.error('Failed to save variant:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!variant) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Thumbnail Variant</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Variant Number */}
          <div className="space-y-2">
            <div className="text-lg font-semibold">#{variant.containerIndex}</div>
          </div>

          {/* Label Input */}
          <div className="space-y-2">
            <Label htmlFor="label">Label (Optional)</Label>
            <Input
              id="label"
              placeholder="e.g., Character Focus, Action Scene"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Add a descriptive label to identify this variant
            </p>
          </div>

          {/* Active/Inactive Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="isActive">Status</Label>
              <p className="text-xs text-muted-foreground">
                {isActive ? 'Active variants will be shown in the app' : 'Inactive variants will not be displayed'}
              </p>
            </div>
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={setEnabled}
            />
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
